import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ApiError, unwrap } from '@/api/envelope';
import {
  listOrganizationPortalUsers,
  resendOrganizationInvitation,
  revokeOrganizationInvitation,
} from '@/api/generated';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { ErrorState } from '@/components/ui/ErrorState';
import { useToast } from '@/components/ui/useToast';
import { invitationIdOf, STATE_LABELS } from '@/lib/portalUsers';
import { testIdPart } from '@/lib/testIds';

import { InviteSuperAdminForm } from './InviteSuperAdminForm';
import { toSuperAdminRows, type SuperAdminRow } from './superAdminActions';

interface SuperAdminsCardProps {
  organizationId: string;
  /**
   * True when this sits inside a CollapsibleCard, which supplies the card and
   * the heading. Without it the component draws its own, so it still stands up
   * on its own in a test or anywhere else.
   */
  embedded?: boolean;
}

/** The query key, in one place, so the mutations below can invalidate it. */
const superAdminsKey = (organizationId: string) =>
  ['organization', organizationId, 'super-admins'] as const;

function expiryText(row: SuperAdminRow): string | null {
  if (!row.expiresAt) return null;
  const when = new Date(row.expiresAt).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  return row.state === 'expired' ? `Expired ${when}` : `Expires ${when}`;
}

/**
 * Who can administer this organization, and the invitations that have gone out.
 *
 * Users and invitations are one list because they are one record on the
 * backend: somebody invited and somebody signing in daily are the same
 * membership at different points in its life. Revoked and removed rows stay on
 * screen, because a staff member wondering why somebody never got in needs to
 * see that it was called off rather than wonder whether it was ever sent.
 *
 * Its own query and its own failure state. A card that cannot load should not
 * take the account page down with it, and a failure shown at the top of the
 * page would read as a complaint about the details form nobody touched.
 *
 * Each row wraps rather than sitting on one line. Written on one line it needed
 * about 480 pixels before it would shrink - a name, an email address, a state
 * and two buttons - and a grid column will not shrink a child below the width
 * it insists on, so the column grew and pushed the whole page sideways. That is
 * why the email address breaks mid-word too: addresses have no spaces in them,
 * so without it one long one sets the minimum width of everything around it.
 */
export function SuperAdminsCard({ organizationId, embedded }: SuperAdminsCardProps) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [revoking, setRevoking] = useState<SuperAdminRow | null>(null);

  const people = useQuery({
    queryKey: superAdminsKey(organizationId),
    queryFn: async () => {
      const reply = await listOrganizationPortalUsers({
        path: { organization_id: organizationId },
        throwOnError: true,
      });
      return toSuperAdminRows(unwrap(reply.data)?.items ?? []);
    },
  });

  const refresh = () =>
    void queryClient.invalidateQueries({ queryKey: superAdminsKey(organizationId) });

  const describe = (error: unknown, fallback: string) =>
    (error instanceof ApiError ? error.serverMessage : null) ?? fallback;

  const resend = useMutation({
    mutationFn: async (row: SuperAdminRow) => {
      const reply = await resendOrganizationInvitation({
        path: { organization_id: organizationId, invitation_id: invitationIdOf(row) },
        throwOnError: true,
      });
      return unwrap(reply.data);
    },
    onSuccess: (data) => {
      refresh();
      toast.show({
        message:
          data?.email_sent === false
            ? 'A fresh link was created but the email could not be sent. Try again.'
            : 'A fresh invitation has been sent. The previous link no longer works.',
        severity: data?.email_sent === false ? 'error' : 'success',
      });
    },
    onError: (error) =>
      toast.show({
        message: describe(error, 'We could not resend that invitation.'),
        severity: 'error',
      }),
  });

  const revoke = useMutation({
    mutationFn: async (row: SuperAdminRow) => {
      await revokeOrganizationInvitation({
        path: { organization_id: organizationId, invitation_id: invitationIdOf(row) },
        throwOnError: true,
      });
    },
    onSuccess: () => {
      setRevoking(null);
      refresh();
      toast.show({ message: 'Invitation revoked.' });
    },
    onError: (error) => {
      setRevoking(null);
      toast.show({
        message: describe(error, 'We could not revoke that invitation.'),
        severity: 'error',
      });
    },
  });

  const rows = people.data ?? [];

  const body = (
    <>
      {people.isPending ? (
        <Stack
          spacing={1}
          aria-label="Loading Super Admins"
          data-testid="super-admins-loading"
        >
          <Skeleton height={44} />
          <Skeleton height={44} />
        </Stack>
      ) : null}

      {people.isError ? (
        <ErrorState
          title={describe(
            people.error,
            'We could not load this organization’s Super Admins.',
          )}
          onRetry={() => void people.refetch()}
          testId="super-admins"
        />
      ) : null}

      {people.isSuccess && rows.length === 0 ? (
        <Typography
          variant="body2"
          color="text.secondary"
          data-testid="super-admins-empty"
        >
          Nobody has been invited yet.
        </Typography>
      ) : null}

      {rows.length > 0 ? (
        <List disablePadding aria-label="Super Admins" data-testid="super-admins-list">
          {rows.map((row, index) => (
            <Box
              key={row.membershipId}
              component="li"
              data-testid={`super-admins-item-${testIdPart(row.membershipId)}`}
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 1,
                py: 1.5,
                borderTop: index === 0 ? 0 : 1,
                borderColor: 'divider',
              }}
            >
              {/* minWidth 0 so this can shrink; without it the address below
                  sets a floor the whole column has to clear. */}
              <Box sx={{ minWidth: 0, flex: '1 1 240px' }}>
                <Typography
                  variant="body2"
                  data-testid="super-admins-item-name-value"
                  sx={{ fontWeight: 500 }}
                >
                  {row.name}
                </Typography>
                <Typography
                  variant="caption"
                  component="p"
                  color="text.secondary"
                  data-testid="super-admins-item-email-value"
                  sx={{ overflowWrap: 'anywhere' }}
                >
                  {row.email}
                  {expiryText(row) ? ` · ${expiryText(row)}` : ''}
                </Typography>
              </Box>

              {/* Text, not a colour. The state has to survive being read
                  aloud and being looked at by somebody who cannot tell
                  amber from green. */}
              <Chip
                size="small"
                label={STATE_LABELS[row.state]}
                color={row.state === 'active' ? 'success' : 'default'}
                data-testid="super-admins-item-state-chip"
              />

              <Stack direction="row" spacing={0.5} sx={{ ml: 'auto' }}>
                {row.canResend ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    // Named with the person, so a screen reader user hears
                    // which row's button they are on rather than the fifth
                    // "Resend" in a row of identical ones.
                    aria-label={`Resend the invitation to ${row.name}`}
                    loading={
                      resend.isPending &&
                      resend.variables?.membershipId === row.membershipId
                    }
                    onClick={() => resend.mutate(row)}
                    data-testid="super-admins-item-resend-button"
                  >
                    Resend
                  </Button>
                ) : null}
                {row.canRevoke ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label={`Revoke the invitation to ${row.name}`}
                    onClick={() => setRevoking(row)}
                    data-testid="super-admins-item-revoke-button"
                  >
                    Revoke
                  </Button>
                ) : null}
              </Stack>
            </Box>
          ))}
        </List>
      ) : null}

      <Divider sx={{ my: 2 }} />

      <InviteSuperAdminForm organizationId={organizationId} onInvited={refresh} />

      <ConfirmDialog
        open={Boolean(revoking)}
        testId="super-admins-revoke-dialog"
        title="Revoke this invitation?"
        description={
          revoking
            ? `${revoking.name} (${revoking.email}) will not be able to use the link ` +
              `they were sent. You can invite them again afterwards.`
            : ''
        }
        confirmLabel="Revoke"
        destructive
        pending={revoke.isPending}
        onConfirm={() => revoking && revoke.mutate(revoking)}
        onCancel={() => setRevoking(null)}
      />
    </>
  );

  if (embedded) return body;

  return (
    <Paper variant="outlined" data-testid="super-admins-card" sx={{ p: 3 }}>
      {/* The same level as the folding card's heading it stands in for, so the
          invitation form's heading below sits one level under it either way. */}
      <Typography variant="h6" component="h2" gutterBottom>
        Super Admins
      </Typography>
      {body}
    </Paper>
  );
}
